import { Component } from "@/types";
import type { PieChartProperties } from "@/types/chart-properties";
import { ChartPropertyChange, commonChartHandler } from "./CommonChartHandler";
import { ChartDataPoint } from "../ChartDataManager";
import { colorMixin, animationMixin, mergeMixinResults } from "./mixins";

/**
 * Pie chart specific command handler
 */
export class PieChartHandler {
  /**
   * Parse pie chart specific commands
   */
  parseCommand(command: string, component: Component): ChartPropertyChange | null {
    // First try common commands
    const commonChanges = commonChartHandler.parseCommand(command, component);

    // Apply mixins (no axis for pie charts)
    const colorChanges = colorMixin.parseCommand(command, component);
    const animationChanges = animationMixin.parseCommand(command, component);

    // Then apply pie-specific commands
    const pieChanges = this.parsePieSpecificCommand(command, component);

    // Merge all changes
    const mixinChanges = mergeMixinResults(colorChanges, animationChanges);

    if (commonChanges || mixinChanges || pieChanges) {
      return {
        ...commonChanges,
        properties: {
          ...commonChanges?.properties,
          ...mixinChanges,
          ...pieChanges?.properties,
        },
        style: {
          ...commonChanges?.style,
          ...pieChanges?.style,
        },
        data: commonChanges?.data || pieChanges?.data,
        type: commonChanges?.type || pieChanges?.type,
      };
    }

    return null;
  }

  private parsePieSpecificCommand(command: string, component: Component): ChartPropertyChange | null {
    const lowerCommand = command.toLowerCase();
    const changes: ChartPropertyChange = {};

    // Donut chart conversion
    if (this.isDonutCommand(lowerCommand)) {
      const donutChanges = this.parseDonutCommand(command, component);
      if (donutChanges) {
        changes.properties = { ...changes.properties, ...donutChanges };
      }
    }

    // Slice labels
    if (this.isLabelCommand(lowerCommand)) {
      const labelChanges = this.parseLabelCommand(command);
      if (labelChanges) {
        changes.properties = { ...changes.properties, ...labelChanges };
      }
    }

    // Exploded slices
    if (this.isExplodeCommand(lowerCommand)) {
      const explodeChanges = this.parseExplodeCommand(command, component);
      if (explodeChanges) {
        changes.properties = { ...changes.properties, ...explodeChanges };
      }
    }

    // Pie rotation
    if (this.isRotationCommand(lowerCommand)) {
      const rotationChanges = this.parseRotationCommand(command, component);
      if (rotationChanges) {
        changes.properties = { ...changes.properties, ...rotationChanges };
      }
    }

    // Slice sorting
    if (this.isSortCommand(lowerCommand)) {
      const sortChanges = this.parseSortCommand(command, component);
      if (sortChanges) {
        changes.properties = { ...changes.properties, ...sortChanges.properties };
        if (sortChanges.data) {
          changes.data = sortChanges.data;
        }
      }
    }

    // 3D effect
    if (this.is3DCommand(lowerCommand)) {
      const threeDChanges = this.parse3DCommand(command);
      if (threeDChanges) {
        changes.properties = { ...changes.properties, ...threeDChanges };
      }
    }

    // Semi-circle/arc
    if (this.isArcCommand(lowerCommand)) {
      const arcChanges = this.parseArcCommand(command);
      if (arcChanges) {
        changes.properties = { ...changes.properties, ...arcChanges };
      }
    }

    // Slice highlighting
    if (this.isHighlightCommand(lowerCommand)) {
      const highlightChanges = this.parseHighlightCommand(command, component);
      if (highlightChanges) {
        changes.properties = { ...changes.properties, ...highlightChanges };
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isDonutCommand(command: string): boolean {
    return /donut|doughnut|ring|hollow|hole|inner/.test(command);
  }

  private parseDonutCommand(command: string, component: Component): Partial<PieChartProperties> | null {
    const changes: Partial<PieChartProperties> = {};
    const currentInnerRadius = typeof component.properties?.innerRadius === 'number'
      ? component.properties.innerRadius
      : 0;

    // Convert to donut
    if (command.includes("donut") || command.includes("doughnut") || command.includes("ring")) {
      if (command.includes("thin")) {
        changes.innerRadius = 70;
      } else if (command.includes("thick") || command.includes("fat")) {
        changes.innerRadius = 30;
      } else {
        changes.innerRadius = 60;
      }
    }

    // Specific inner radius
    const radiusMatch = command.match(/inner\s+radius\s+(?:of\s+)?(\d+)/i);
    if (radiusMatch) {
      changes.innerRadius = parseInt(radiusMatch[1]);
    }

    // Hole size percentage
    const holeMatch = command.match(/(\d+)\s*%\s+hole/i);
    if (holeMatch) {
      const percentage = parseInt(holeMatch[1]);
      changes.innerRadius = percentage;
    }

    // Convert back to pie
    if (command.includes("solid") || command.includes("filled") || (command.includes("no") && command.includes("hole"))) {
      changes.innerRadius = 0;
    }

    // Adjust hole size relatively
    if (command.includes("bigger hole") || command.includes("larger hole")) {
      changes.innerRadius = Math.min(90, currentInnerRadius + 20);
    } else if (command.includes("smaller hole") || command.includes("tiny hole")) {
      changes.innerRadius = Math.max(0, currentInnerRadius - 20);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isLabelCommand(command: string): boolean {
    return /label|percentage|percent|value|name|text|callout/.test(command);
  }

  private parseLabelCommand(command: string): Partial<PieChartProperties> | null {
    const changes: Partial<PieChartProperties> = {};

    // Show/hide labels
    if (command.includes("show") && (command.includes("label") || command.includes("percentage") || command.includes("value"))) {
      changes.showLabels = true;

      // Label format
      if (command.includes("percentage") || command.includes("percent")) {
        changes.labelFormat = "percentage";
        changes.showPercentage = true;
      } else if (command.includes("value") && command.includes("name")) {
        changes.labelFormat = "both";
      } else if (command.includes("name") || command.includes("category")) {
        changes.labelFormat = "name";
      } else if (command.includes("value")) {
        changes.labelFormat = "value";
      }
    } else if (command.includes("hide") && (command.includes("label") || command.includes("percentage"))) {
      changes.showLabels = false;
    }

    // Label position
    if (command.includes("outside") || command.includes("external") || command.includes("callout")) {
      changes.labelPosition = "outside";
      changes.showLabelLine = true;
    } else if (command.includes("inside") || command.includes("internal") || command.includes("center")) {
      changes.labelPosition = "inside";
      changes.showLabelLine = false;
    }

    // Decimal places for percentages
    const decimalMatch = command.match(/(\d+)\s+decimal/i);
    if (decimalMatch) {
      changes.labelPrecision = parseInt(decimalMatch[1]);
    }

    // Smart labels (avoid overlap)
    if (command.includes("smart") || command.includes("no overlap")) {
      changes.smartLabels = true;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isExplodeCommand(command: string): boolean {
    return /explode|pull|separate|highlight|emphasize/.test(command);
  }

  private parseExplodeCommand(command: string, component: Component): Partial<PieChartProperties> | null {
    const changes: Partial<PieChartProperties> = {};
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];

    // Explode all slices
    if (command.includes("explode all") || command.includes("separate all")) {
      changes.explodeOffset = 10;
      changes.explodeAll = true;
    }

    // Explode specific slice
    const sliceMatch = command.match(/explode\s+(?:slice\s+)?["']([^"']+)["']/i) ||
                      command.match(/highlight\s+["']([^"']+)["']/i);
    if (sliceMatch && currentData.length > 0) {
      const sliceName = sliceMatch[1];
      const sliceIndex = currentData.findIndex(d => d.name === sliceName);
      if (sliceIndex >= 0) {
        changes.explodedSlices = [sliceIndex];
        changes.explodeOffset = 10;
      }
    }

    // Explode largest/smallest slice
    if (command.includes("largest") || command.includes("biggest")) {
      if (currentData.length > 0) {
        const maxIndex = currentData.reduce((maxIdx, item, idx, arr) =>
          (item.value ?? 0) > (arr[maxIdx]?.value ?? 0) ? idx : maxIdx, 0);
        changes.explodedSlices = [maxIndex];
        changes.explodeOffset = 10;
      }
    } else if (command.includes("smallest") || command.includes("tiniest")) {
      if (currentData.length > 0) {
        const minIndex = currentData.reduce((minIdx, item, idx, arr) =>
          (item.value ?? 0) < (arr[minIdx]?.value ?? 0) ? idx : minIdx, 0);
        changes.explodedSlices = [minIndex];
        changes.explodeOffset = 10;
      }
    }

    // Explode offset amount
    const offsetMatch = command.match(/explode\s+(?:by\s+)?(\d+)/i);
    if (offsetMatch) {
      changes.explodeOffset = parseInt(offsetMatch[1]);
    }

    // Reset explosion
    if (command.includes("reset") || command.includes("unexplode") || command.includes("normal")) {
      changes.explodedSlices = [];
      changes.explodeOffset = 0;
      changes.explodeAll = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isRotationCommand(command: string): boolean {
    return /rotate|turn|spin|angle|start/.test(command);
  }

  private parseRotationCommand(command: string, component: Component): Partial<PieChartProperties> | null {
    const changes: Partial<PieChartProperties> = {};
    const currentStartAngle = typeof component.properties?.startAngle === 'number'
      ? component.properties.startAngle
      : 0;

    // Specific angle
    const angleMatch = command.match(/(?:rotate|turn|spin)\s+(?:by\s+)?(-?\d+)/i) ||
                      command.match(/start\s+(?:at\s+)?(-?\d+)/i);
    if (angleMatch) {
      const angle = parseInt(angleMatch[1]);
      if (command.includes("by")) {
        changes.startAngle = (currentStartAngle + angle) % 360;
      } else {
        changes.startAngle = angle;
      }
    }

    // Common rotations
    if (command.includes("quarter") || command.includes("90")) {
      changes.startAngle = (currentStartAngle + 90) % 360;
    } else if (command.includes("half") || command.includes("180")) {
      changes.startAngle = (currentStartAngle + 180) % 360;
    } else if (command.includes("top") || command.includes("north")) {
      changes.startAngle = -90;
    } else if (command.includes("right") || command.includes("east")) {
      changes.startAngle = 0;
    } else if (command.includes("bottom") || command.includes("south")) {
      changes.startAngle = 90;
    } else if (command.includes("left") || command.includes("west")) {
      changes.startAngle = 180;
    }

    // Continuous rotation (animation)
    if (command.includes("spin") && command.includes("continuous")) {
      changes.animateRotation = true;
      changes.rotationSpeed = 2; // degrees per frame
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isSortCommand(command: string): boolean {
    return /sort|order|arrange|largest first|smallest first/.test(command);
  }

  private parseSortCommand(command: string, component: Component): ChartPropertyChange | null {
    const changes: ChartPropertyChange = {};
    const pieProps: Partial<PieChartProperties> = {};
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];

    if (currentData.length === 0) return null;

    let sortedData: ChartDataPoint[] | null = null;

    // Sort by value
    if (command.includes("largest first") || command.includes("descending") || command.includes("biggest first")) {
      sortedData = [...currentData].sort((a, b) => (b.value || 0) - (a.value || 0));
      pieProps.sortOrder = "descending";
    } else if (command.includes("smallest first") || command.includes("ascending") || command.includes("smallest first")) {
      sortedData = [...currentData].sort((a, b) => (a.value || 0) - (b.value || 0));
      pieProps.sortOrder = "ascending";
    } else if (command.includes("alphabetical") || command.includes("by name")) {
      sortedData = [...currentData].sort((a, b) => {
        const nameA = String(a.name || "");
        const nameB = String(b.name || "");
        return nameA.localeCompare(nameB);
      });
      pieProps.sortOrder = "alphabetical";
    } else if (command.includes("original") || command.includes("unsort") || command.includes("reset order")) {
      pieProps.sortOrder = "none";
    }

    if (sortedData) {
      changes.data = sortedData;
    }

    if (Object.keys(pieProps).length > 0) {
      changes.properties = pieProps;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private is3DCommand(command: string): boolean {
    return /3d|three.dimensional|depth|perspective/.test(command);
  }

  private parse3DCommand(command: string): Partial<PieChartProperties> | null {
    const changes: Partial<PieChartProperties> = {};

    if (command.includes("3d") || command.includes("three") || command.includes("perspective")) {
      changes.enable3D = true;

      // Depth amount
      const depthMatch = command.match(/depth\s+(?:of\s+)?(\d+)/i);
      if (depthMatch) {
        changes.depth3D = parseInt(depthMatch[1]);
      } else {
        changes.depth3D = 20;
      }

      // Perspective angle
      if (command.includes("shallow") || command.includes("subtle")) {
        changes.perspectiveAngle = 15;
      } else if (command.includes("deep") || command.includes("dramatic")) {
        changes.perspectiveAngle = 45;
      } else {
        changes.perspectiveAngle = 30;
      }
    }

    // Disable 3D
    if (command.includes("flat") || command.includes("2d") || (command.includes("no") && command.includes("3d"))) {
      changes.enable3D = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isArcCommand(command: string): boolean {
    return /semi.circle|half.circle|arc|gauge|partial/.test(command);
  }

  private parseArcCommand(command: string): Partial<PieChartProperties> | null {
    const changes: Partial<PieChartProperties> = {};

    if (command.includes("semi") || command.includes("half")) {
      changes.startAngle = 180;
      changes.endAngle = 360;
    } else if (command.includes("quarter")) {
      changes.startAngle = 0;
      changes.endAngle = 90;
    } else if (command.includes("three quarter")) {
      changes.startAngle = 0;
      changes.endAngle = 270;
    }

    // Custom arc
    const arcMatch = command.match(/arc\s+from\s+(-?\d+)\s+to\s+(-?\d+)/i);
    if (arcMatch) {
      changes.startAngle = parseInt(arcMatch[1]);
      changes.endAngle = parseInt(arcMatch[2]);
    }

    // Gauge mode (like a speedometer)
    if (command.includes("gauge") || command.includes("meter")) {
      changes.startAngle = 225;
      changes.endAngle = -45;
      changes.innerRadius = 60;
      changes.gaugeMode = true;
    }

    // Full circle
    if (command.includes("full circle") || command.includes("complete")) {
      changes.startAngle = 0;
      changes.endAngle = 360;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isHighlightCommand(command: string): boolean {
    return /highlight|emphasize|focus|dim|fade/.test(command);
  }

  private parseHighlightCommand(command: string, component: Component): Partial<PieChartProperties> | null {
    const changes: Partial<PieChartProperties> = {};
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];

    // Highlight specific slice
    const highlightMatch = command.match(/highlight\s+["']([^"']+)["']/i);
    if (highlightMatch && currentData.length > 0) {
      const sliceName = highlightMatch[1];
      const sliceIndex = currentData.findIndex(d => d.name === sliceName);
      if (sliceIndex >= 0) {
        changes.highlightedSlices = [sliceIndex];
        changes.dimOthers = true;
        changes.dimOpacity = 0.3;
      }
    }

    // Highlight multiple slices
    if (command.includes("highlight all") || command.includes("emphasize all")) {
      changes.highlightedSlices = currentData.map((_, idx) => idx);
      changes.dimOthers = false;
    }

    // Dim/fade others
    if (command.includes("dim others") || command.includes("fade others")) {
      changes.dimOthers = true;
      const opacityMatch = command.match(/opacity\s+(?:of\s+)?(\d+(?:\.\d+)?)/i);
      if (opacityMatch) {
        changes.dimOpacity = parseFloat(opacityMatch[1]);
      } else {
        changes.dimOpacity = 0.3;
      }
    }

    // Reset highlighting
    if (command.includes("reset") || command.includes("clear highlight") || command.includes("normal")) {
      changes.highlightedSlices = [];
      changes.dimOthers = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  /**
   * Get pie chart specific suggestions
   */
  getSuggestions(component: Component): string[] {
    const suggestions: string[] = [
      ...commonChartHandler.getSuggestions(component),
      ...colorMixin.getSuggestions(component),
    ];

    // Pie-specific suggestions
    const innerRadius = typeof component.properties?.innerRadius === 'number'
      ? component.properties.innerRadius
      : 0;

    if (innerRadius === 0) {
      suggestions.push("Convert to donut chart", "Make a thin ring chart");
    } else {
      suggestions.push("Make the hole bigger", "Convert to solid pie chart");
    }

    if (!component.properties?.showLabels) {
      suggestions.push("Show percentage labels", "Display slice names");
    } else {
      suggestions.push("Hide labels", "Show labels outside");
    }

    suggestions.push(
      "Explode the largest slice",
      "Sort by size descending",
      "Rotate by 90 degrees",
      "Make it a semi-circle",
      "Add 3D effect",
      "Highlight slice 'Sales'"
    );

    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];

    if (currentData.length > 0) {
      const explodedSlices = component.properties?.explodedSlices;
      if (!Array.isArray(explodedSlices) || explodedSlices.length === 0) {
        suggestions.push(`Explode slice "${currentData[0].name}"`);
      }
    }

    return suggestions;
  }
}

export const pieChartHandler = new PieChartHandler();
